import React, { useState, useRef } from 'react'
import { Controller } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { UseFormReturn } from 'react-hook-form/dist/types'
import { getError } from './FormTextField'
import {
  S,
  ReactSelectOption,
  DropdownIndicator,
  IndicatorSeparator,
} from './FormSelect'
import AsyncSelect from 'react-select/async'
import { StylesConfig } from 'react-select'

const MESSAGE = {
  initial: 'Type to search...',
  noOption: 'No options, try another name',
  networkError: 'Network error, please try again',
}
interface FormSelectProps {
  useForm: UseFormReturn<any>
  isMulti?: boolean
  label: string
  name: string
  rules?: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  disabled?: boolean
  required?: boolean
  fetchRequest: (search: string) => Promise<ReactSelectOption[]>
}

export const selectStyle: StylesConfig = {
  control: (provided) => ({
    ...provided,
    padding: '.25rem .5rem',
  }),
  menuList: (provided) => ({
    ...provided,
    zIndex: '2',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

const FormSelect = ({
  useForm,
  isMulti,
  label,
  name,
  rules,
  disabled,
  required,
  fetchRequest,
}: FormSelectProps) => {
  const [thisFocused, setFocus] = useState(false)
  const value = useForm.watch(name)
  const error = getError(useForm.formState.errors, name)

  const onFocus = () => {
    setFocus(true)
  }
  const onBlur = () => {
    setFocus(false)
  }
  const messageRef = useRef<string>(MESSAGE.noOption)

  let timer = useRef<unknown>()
  const loadOptionsWithDebounce = (
    keyword: string,
    callback: (options: ReactSelectOption[]) => void
  ) => {
    clearTimeout(timer.current as number)
    // debounce 300ms
    timer.current = setTimeout(() => {
      fetchRequest(keyword)
        .then((options) => {
          if (!options?.length) {
            messageRef.current = MESSAGE.noOption
          }
          callback(options)
        })
        .catch((err) => {
          messageRef.current = MESSAGE.networkError
          callback([])
        })
    }, 250)
  }

  return (
    <Controller
      name={name as `${string}`}
      control={useForm.control}
      rules={rules}
      render={({ field }) => (
        <S.Container>
          <S.Label
            floating={thisFocused || value != null}
            error={error != null}
          >
            {error == null ? label : `${label} is ${error.type}`}{' '}
            {required && <span>*</span>}
          </S.Label>
          <S.Wrapper error={error != null}>
            <AsyncSelect
              {...field}
              placeholder=""
              isClearable
              onFocus={onFocus}
              onBlur={onBlur}
              isDisabled={!!disabled}
              isMulti={!!isMulti}
              loadOptions={loadOptionsWithDebounce}
              noOptionsMessage={({ inputValue }) =>
                inputValue ? messageRef.current : MESSAGE.initial
              }
              components={{
                DropdownIndicator,
                IndicatorSeparator,
              }}
              styles={selectStyle}
              menuPortalTarget={document.body}
            />
          </S.Wrapper>
        </S.Container>
      )}
    />
  )
}

export default FormSelect
